
"use client";
import { useRef, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import toast from "react-hot-toast";

export const ApplyForm = ({ jobData }) => {
	const resumeInputRef = useRef(null);
	const fullNameInputRef = useRef(null);
	const emailInputRef = useRef(null);
	const phoneInputRef = useRef(null);
	const positionInputRef = useRef(null);
	const [currentErrorField, setCurrentErrorField] = useState("");

	const formik = useFormik({
		initialValues: {
			fullName: "",
			email: "",
			phone: "",
			position: jobData.title,
			resume: null,
			status: "",
		},
		validateOnChange: false,
		validateOnBlur: true,
		onSubmit: async (values, { setSubmitting, resetForm }) => {
			try {
				let formData = new FormData();
				for (let value in values) {
					formData.append(value, values[value] ?? "");
				}
				const res = await fetch(`${process.env.NEXT_PUBLIC_LIVE_URL}/api/apply`,
					{
						method: "POST",
						body: formData,
					}
				);
				if (res.status === 201) {
					toast.success("Your application has been submitted successfully.");
					resetForm();
					setCurrentErrorField("");
					if (resumeInputRef.current) {
						resumeInputRef.current.value = "";
					}
				} else {
					toast.error("Something went wrong. Please try again");
				}
			} catch (error) {
				toast.error("Something went wrong. Please try again");
			} finally {
				setSubmitting(false);
			}
		},
		validationSchema: yup.object({
			email: yup.string().email("Please enter a valid email address").required("Please enter your email address"),
			fullName: yup.string().trim().min(2, "Name must be at least 2 characters").required("Please enter your full name"),
			phone: yup.string().required("Please enter your phone number").matches(/^\+?[\d\s()-]+$/, "Please enter a valid phone number").test("phone-length", "Phone number must contain 7 to 15 digits", (value) => {
				const digits = (value || "").replace(/\D/g, "");
				return digits.length >= 7 && digits.length <= 15;
			}),
			position: yup.string().required("Position is required"),
			resume: yup.mixed().required("Please upload your resume").test("file-type", "Only PDF, DOC and DOCX files are allowed", (file) => {
				if (!file) return false;
				return /\.(pdf|doc|docx)$/i.test(file.name);
			}),
		}),
	});

	const handleFormSubmit = async (e) => {
		e.preventDefault();

		const errors = await formik.validateForm();
		const fields = ["fullName", "email", "phone", "position", "resume"];
		const firstErrorField = fields.find((field) => errors[field]);

		if (firstErrorField) {
			setCurrentErrorField(firstErrorField);
			await formik.setTouched({ [firstErrorField]: true });

			const fieldRefs = {
				fullName: fullNameInputRef,
				email: emailInputRef,
				phone: phoneInputRef,
				position: positionInputRef,
				resume: resumeInputRef,
			};

			setTimeout(() => {
				fieldRefs[firstErrorField]?.current?.focus();
			}, 0);

			return;
		}

		setCurrentErrorField("");
		formik.handleSubmit(e);
	};

	const handleFieldBlur = async (field) => {
		await formik.setFieldTouched(field, true, false);
		const errors = await formik.validateForm();
		const fields = ["fullName", "email", "phone", "position", "resume"];
		const firstErrorField = fields.find((item) => errors[item]);

		setCurrentErrorField(firstErrorField || "");
	};

	return (
		<>
			<form onSubmit={handleFormSubmit} className="py-6" noValidate>
				<div className="input-wrapper">
					<input ref={fullNameInputRef} id="fullName" type="text" className={`input ${currentErrorField === "fullName" && formik.errors.fullName ? "error-input" : ""}`} {...formik.getFieldProps("fullName")} onChange={(e) => { formik.handleChange(e); if (currentErrorField === "fullName") { formik.setFieldValue("fullName", e.target.value, true).then(() => { if (!formik.errors.fullName) setCurrentErrorField(""); }); } }} onBlur={() => handleFieldBlur("fullName")} placeholder="Full Name*" />
					{currentErrorField === "fullName" && formik.errors.fullName ? (
						<div className="error-text">{formik.errors.fullName}</div>
					) : null}
				</div>

				<div className="input-wrapper">
					<input ref={emailInputRef} id="email" type="email" className={`input ${currentErrorField === "email" && formik.errors.email ? "error-input" : ""}`} {...formik.getFieldProps("email")} onBlur={() => handleFieldBlur("email")} placeholder="Email*" />
					{currentErrorField === "email" && formik.errors.email ? (
						<div className="error-text">{formik.errors.email}</div>
					) : null}
				</div>

				<div className="input-wrapper">
					<input ref={phoneInputRef} id="phone" type="text" className={`input ${currentErrorField === "phone" && formik.errors.phone ? "error-input" : ""}`} {...formik.getFieldProps("phone")} onBlur={() => handleFieldBlur("phone")} placeholder="Phone*" />
					{currentErrorField === "phone" && formik.errors.phone ? (
						<div className="error-text">{formik.errors.phone}</div>
					) : null}
				</div>

				<div className="input-wrapper">
					<input ref={positionInputRef} id="position" type="text" className={`input ${currentErrorField === "position" && formik.errors.position ? "error-input" : ""}`} {...formik.getFieldProps("position")} onBlur={() => handleFieldBlur("position")} placeholder="position*" readOnly />
					{currentErrorField === "position" && formik.errors.position ? (
						<div className="error-text">{formik.errors.position}</div>
					) : null}
				</div>

				<div className="input-wrapper pt-6">
					<input ref={resumeInputRef} id="resume" name="resume" type="file" accept=".pdf,.doc,.docx" onChange={(e) => { const file = e.currentTarget.files?.[0] || null; formik.setFieldValue("resume", file); formik.setFieldTouched("resume", true, false); if (currentErrorField === "resume" && file && /\.(pdf|doc|docx)$/i.test(file.name)) { setCurrentErrorField(""); } }} onBlur={() => handleFieldBlur("resume")} className={`w-60 text-gray-400 font-semibold text-sm bg-white border file:cursor-pointer cursor-pointer file:border-0 file:py-3 file:px-4 file:mr-4 file:bg-gray-100 file:hover:bg-gray-200 file:text-gray-500 rounded ${currentErrorField === "resume" && formik.errors.resume ? "error-input" : ""}`} />
					<p className="text-xs text-gray-400 mt-2">Accepted formats: PDF, Word (DOC, DOCX).</p>
					{currentErrorField === "resume" && formik.errors.resume ? (
						<div className="error-text">{formik.errors.resume}</div>
					) : null}
				</div>

				<div className="">
					<button type="submit" className={`submit submit--full ${formik.isSubmitting ? "submit--disabled" : ""}`} disabled={formik.isSubmitting}>
						{formik.isSubmitting ? (
							<span>Please wait...</span>
						) : ("Apply Now")}
						{formik.isSubmitting ? (<span>{/* <Loader size="small" /> */}</span>) : ("")}
					</button>
				</div>
			</form>
		</>
	);
};
